import { useState, useCallback } from 'react';
import axios from 'axios';
import { MonthlyDynamicsRow, PharmacyDistRow, ProductDynamicsRow, JournalMovementRow } from '../types';
import { API_BASE } from '../constants';

export interface AnalyticsState {
  monthlyData: MonthlyDynamicsRow[];
  expiryData: MonthlyDynamicsRow[];
  pharmacyDistData: PharmacyDistRow[];
  productDynData: ProductDynamicsRow[];
  movementsData: JournalMovementRow[];
  analyticsLoading: boolean;
  analyticsLoaded: boolean;
  fetchAnalytics: () => Promise<void>;
  fetchProductDynamics: (params: { pharmacyNum?: string; materialName?: string }) => Promise<void>;
}

/**
 * Управляет данными аналитики журнала:
 * - помесячная динамика сумм (по дате записи и по срокам годности — График 1)
 * - распределение по аптекам (График 2 — Pie + Bar)
 * - динамика по конкретному товару / аптеке (График 3) + лог движений из ZF_Journal_Movements
 *
 * `fetchAnalytics` вызывается один раз при первом открытии вкладки (lazy load).
 * username читается из localStorage и передаётся на бэкенд для ограничения данных
 * привязанными к пользователю аптеками.
 */
export function useAnalytics(): AnalyticsState {
  const [monthlyData, setMonthlyData] = useState<MonthlyDynamicsRow[]>([]);
  const [expiryData, setExpiryData] = useState<MonthlyDynamicsRow[]>([]);
  const [pharmacyDistData, setPharmacyDistData] = useState<PharmacyDistRow[]>([]);
  const [productDynData, setProductDynData] = useState<ProductDynamicsRow[]>([]);
  const [movementsData, setMovementsData] = useState<JournalMovementRow[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    if (analyticsLoaded) return;
    setAnalyticsLoading(true);
    // username передаётся на бэкенд для фильтрации по привязанным аптекам
    const username = localStorage.getItem('username') ?? undefined;
    try {
      const [monthly, expiry, pharmDist] = await Promise.all([
        axios.get(`${API_BASE}/journal/analytics/monthly-dynamics`, { params: { username } }),
        axios.get(`${API_BASE}/journal/analytics/expiry-dynamics`, { params: { username } }),
        axios.get(`${API_BASE}/journal/analytics/pharmacy-distribution`, { params: { username } }),
      ]);
      if (monthly.data?.success) setMonthlyData(monthly.data.data || []);
      if (expiry.data?.success) setExpiryData(expiry.data.data || []);
      if (pharmDist.data?.success) setPharmacyDistData(pharmDist.data.data || []);
      setAnalyticsLoaded(true);
    } catch (e) {
      console.error('Analytics fetch error:', e);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsLoaded]);

  const fetchProductDynamics = useCallback(async ({
    pharmacyNum,
    materialName,
  }: { pharmacyNum?: string; materialName?: string }) => {
    // username передаётся на бэкенд для фильтрации по привязанным аптекам
    const username = localStorage.getItem('username') ?? undefined;
    try {
      const resp = await axios.get(`${API_BASE}/journal/analytics/product-dynamics`, {
        params: {
          username,
          pharmacyNum: pharmacyNum !== 'all' ? pharmacyNum : undefined,
          materialName: materialName ?? undefined,
        },
      });
      if (resp.data?.success) {
        setProductDynData(resp.data.data || []);
        setMovementsData(resp.data.movements || []);
      }
    } catch (e) {
      console.error('Product dynamics fetch error:', e);
    }
  }, []);

  return {
    monthlyData, expiryData, pharmacyDistData, productDynData, movementsData,
    analyticsLoading, analyticsLoaded,
    fetchAnalytics, fetchProductDynamics,
  };
}
